import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { authAPI } from '../../api/auth';
import { legalAPI, type LegalAcceptanceItem, type LegalDocumentType } from '../../api/legal';
import DarkModeToggle from '../DarkModeToggle';
import { EXTERNAL_LINKS } from '../../config/constants';
import { logger } from '../../utils/logger';

import styles from './LoginForm.module.css'; // Reuse login styles

export default function RegisterForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const { register, isLoading, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/auth/continue', { replace: true, state: { from: location.state?.from } });
    }
  }, [isAuthenticated, navigate, location]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.organizationName.trim()) {
      errors.organizationName = 'Organization name is required';
    } else if (formData.organizationName.trim().length < 2) {
      errors.organizationName = 'Organization name must be at least 2 characters';
    }
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain uppercase, lowercase, and number';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (!acceptTerms) {
      errors.terms = 'You must accept the terms and conditions';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setGoogleLoading(true);
      const { url } = await authAPI.getGoogleAuthUrl();
      sessionStorage.removeItem('auth_google_return');
      if (typeof location.state?.from?.pathname === 'string') {
        const { pathname, search, hash } = location.state.from;
        sessionStorage.setItem('auth_google_return', JSON.stringify({ pathname, search, hash }));
      }
      window.location.href = url;
    } catch (err) {
      logger.error('Failed to start Google sign-up:', err);
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // PR6: fetch the currently-published ToS + Privacy versions and pass
      // them into /auth/register so the backend records acceptance in the
      // same transaction as the user row. The combined checkbox above gates
      // submission. If the legal fetch itself fails we still attempt
      // registration without the acceptances - the LegalAcknowledgmentGate
      // inside RequireAuth will catch the user on the next page and re-prompt.
      let acceptances: LegalAcceptanceItem[] = [];
      try {
        const cur = await legalAPI.getCurrent();
        acceptances = (['tos', 'privacy'] as LegalDocumentType[])
          .map((dt) => cur.documents[dt])
          .filter((d): d is NonNullable<typeof d> => !!d && d.published && !!d.version && !!d.sha256)
          .map((d) => ({ document_type: d.document_type, version: d.version!, sha256: d.sha256! }));
      } catch (fetchErr) {
        logger.warn('Pre-register legal fetch failed; gate will re-prompt:', fetchErr);
      }
      await register(
        formData.email, formData.password, formData.name,
        formData.organizationName, acceptances.length > 0 ? acceptances : undefined,
      );
      // Redirect will happen automatically via useEffect when isAuthenticated changes
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  // Don't render if already authenticated (will redirect)
  if (isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <div className={styles.header}>
            <h1 className={styles.title}>Redirecting...</h1>
            <p className={styles.subtitle}>You are already logged in</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.themeToggle}>
        <DarkModeToggle />
      </div>
      <div className={styles.formWrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Create Account</h1>
          <p className={styles.subtitle}>Join LazySEM and start optimizing your content</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="name" className={styles.label}>
              Full Name
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`${styles.input} ${formErrors.name ? styles.error : ''}`}
              placeholder="Enter your full name"
              disabled={isLoading}
              autoComplete="name"
            />
            {formErrors.name && (
              <span className={styles.errorMessage}>{formErrors.name}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="organizationName" className={styles.label}>
              Organization Name
            </label>
            <input
              id="organizationName"
              type="text"
              value={formData.organizationName}
              onChange={(e) => handleInputChange('organizationName', e.target.value)}
              className={`${styles.input} ${formErrors.organizationName ? styles.error : ''}`}
              placeholder="Enter your organization name"
              disabled={isLoading}
              autoComplete="organization"
            />
            {formErrors.organizationName && (
              <span className={styles.errorMessage}>{formErrors.organizationName}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`${styles.input} ${formErrors.email ? styles.error : ''}`}
              placeholder="Enter your email"
              disabled={isLoading}
              autoComplete="email"
            />
            {formErrors.email && (
              <span className={styles.errorMessage}>{formErrors.email}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <div className={styles.passwordWrapper}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`${styles.input} ${formErrors.password ? styles.error : ''}`}
                placeholder="Create a strong password"
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {formErrors.password && (
              <span className={styles.errorMessage}>{formErrors.password}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={`${styles.input} ${formErrors.confirmPassword ? styles.error : ''}`}
              placeholder="Confirm your password"
              disabled={isLoading}
              autoComplete="new-password"
            />
            {formErrors.confirmPassword && (
              <span className={styles.errorMessage}>{formErrors.confirmPassword}</span>
            )}
          </div>

          <div className={styles.termsSection}>
            <label className={styles.termsLabel}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                disabled={isLoading}
                className={styles.termsCheckbox}
              />
              <span className={styles.termsText}>
                I agree to the{' '}
                <a href={EXTERNAL_LINKS.TERMS_OF_SERVICE} className={styles.termsLink} target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={EXTERNAL_LINKS.PRIVACY_POLICY} className={styles.termsLink} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>

          {formErrors.terms && (
            <div className={styles.errorAlert}>
              <span className={styles.errorIcon}>⚠️</span>
              {formErrors.terms}
            </div>
          )}

          {error && (
            <div className={styles.errorAlert}>
              <span className={styles.errorIcon}>⚠️</span>
              {error}
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className={styles.loadingSpinner}>Creating Account...</span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <p className={styles.signupPrompt}>
            Already have an account?{' '}
            <Link to="/login" state={{ from: location.state?.from }} className={styles.signupLink}>
              Sign in
            </Link>
          </p>
        </div>

        <div className={styles.divider}>
          <span className={styles.dividerText}>or</span>
        </div>

        <div className={styles.socialLogin}>
          <button
            className={styles.socialButton}
            disabled={isLoading || googleLoading}
            onClick={handleGoogleSignUp}
            type="button"
          >
            <svg className={styles.googleIcon} viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {googleLoading ? 'Connecting...' : 'Continue with Google'}
          </button>
        </div>
      </div>
    </div>
  );
}
